<template>
  <div class="relative min-h-screen flex overflow-hidden">
    <!-- Animated background (covers left panel) -->
    <div class="absolute inset-0 bg-primary-dark">
      <div class="orb orb-1"></div>
      <div class="orb orb-2"></div>
      <div class="orb orb-3"></div>
      <div class="absolute inset-0 dot-grid opacity-[0.03]"></div>
    </div>

    <!-- Left Panel: Branding -->
    <div class="hidden lg:flex flex-1 relative z-10 items-center justify-center p-12 xl:p-16">
      <div class="max-w-lg">
        <img src="@/assets/images/sellhub-logo.png" alt="SellHub" class="h-14 w-auto mb-10 brightness-0 invert opacity-90" />

        <h1 class="text-4xl xl:text-5xl font-bold text-white leading-tight mb-4">
          Gérez votre activité <span class="text-secondary">en toute simplicité</span>
        </h1>
        <p class="text-white text-lg mb-12 leading-relaxed">
          SellHub vous offre tous les outils pour piloter votre business, de la gestion de stock à la facturation.
        </p>

        <div class="space-y-5">
          <div class="flex items-start gap-4 group">
            <div class="w-11 h-11 rounded-xl bg-white border border-gray-200 flex items-center justify-center flex-shrink-0 group-hover:bg-gray-100 transition-colors">
              <i class="fas fa-chart-line text-primary text-sm"></i>
            </div>
            <div>
              <p class="text-white font-semibold text-sm mb-0.5">Tableau de bord intelligent</p>
              <p class="text-white text-sm">Suivez vos performances et KPIs en temps réel</p>
            </div>
          </div>

          <div class="flex items-start gap-4 group">
            <div class="w-11 h-11 rounded-xl bg-white border border-gray-200 flex items-center justify-center flex-shrink-0 group-hover:bg-gray-100 transition-colors">
              <i class="fas fa-box-open text-primary text-sm"></i>
            </div>
            <div>
              <p class="text-white font-semibold text-sm mb-0.5">Gestion de stock avancée</p>
              <p class="text-white text-sm">Automatisez votre inventaire et réapprovisionnement</p>
            </div>
          </div>

          <div class="flex items-start gap-4 group">
            <div class="w-11 h-11 rounded-xl bg-white border border-gray-200 flex items-center justify-center flex-shrink-0 group-hover:bg-gray-100 transition-colors">
              <i class="fas fa-file-invoice-dollar text-primary text-sm"></i>
            </div>
            <div>
              <p class="text-white font-semibold text-sm mb-0.5">Facturation simplifiée</p>
              <p class="text-white text-sm">Créez et envoyez vos factures en quelques clics</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Panel: Solid white form -->
    <div class="relative z-10 w-full lg:w-[480px] xl:w-[520px] flex-shrink-0 bg-white flex items-center justify-center p-6 sm:p-10 shadow-2xl">
      <div class="w-full max-w-[400px]">
        <!-- Mobile logo -->
        <div class="lg:hidden flex justify-center mb-8">
          <img src="@/assets/images/sellhub-logo.png" alt="SellHub" class="h-10 w-auto" />
        </div>

        <div class="mb-8">
          <h2 class="text-2xl font-bold text-gray-900 mb-2">Bon retour !</h2>
          <p class="text-gray-500 text-sm">Connectez-vous à votre espace de gestion</p>
        </div>

        <!-- Error -->
        <div v-if="error" class="flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 text-sm">
          <i class="fas fa-exclamation-circle mt-0.5 text-red-500"></i>
          <span>{{ error }}</span>
        </div>

        <form @submit.prevent="handleLogin" class="space-y-5">
          <!-- Email -->
          <div>
            <label class="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Email</label>
            <div class="relative">
              <i class="fas fa-envelope absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                v-model="form.email"
                type="email"
                placeholder="votre@email.com"
                required
                :class="[
                  'w-full pl-11 pr-4 py-3 bg-gray-50 border rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all',
                  validationErrors.email ? 'border-red-400' : 'border-gray-200'
                ]"
              />
            </div>
            <p v-if="validationErrors.email" class="text-red-500 text-xs mt-1.5">{{ validationErrors.email[0] }}</p>
          </div>

          <!-- Password -->
          <div>
            <label class="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Mot de passe</label>
            <div class="relative">
              <i class="fas fa-lock absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                v-model="form.password"
                :type="showPassword ? 'text' : 'password'"
                placeholder="••••••••"
                required
                :class="[
                  'w-full pl-11 pr-12 py-3 bg-gray-50 border rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all',
                  validationErrors.password ? 'border-red-400' : 'border-gray-200'
                ]"
              />
              <button type="button" class="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors" @click.prevent="togglePasswordVisibility">
                <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'" class="text-sm"></i>
              </button>
            </div>
            <p v-if="validationErrors.password" class="text-red-500 text-xs mt-1.5">{{ validationErrors.password[0] }}</p>
          </div>

          <!-- Forgot -->
          <div class="text-right">
            <router-link to="/forgot-password" class="text-sm text-primary hover:text-primary-dark font-medium transition-colors">
              Mot de passe oublié ?
            </router-link>
          </div>

          <!-- Submit -->
          <button
            type="submit"
            :disabled="isLoading"
            class="w-full py-3.5 bg-primary hover:bg-primary-dark text-white font-semibold text-sm rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-primary/25 hover:shadow-primary/40 flex items-center justify-center gap-2"
          >
            <i v-if="isLoading" class="fas fa-spinner fa-spin text-sm"></i>
            {{ isLoading ? 'Connexion...' : 'Se connecter' }}
          </button>
        </form>

        <!-- Footer -->
        <div class="text-center mt-8 pt-6 border-t border-gray-100 space-y-1">
          <p class="text-gray-400 text-xs">
            © Version {{ version }} - {{ new Date().getFullYear() }} SellHub. Tous droits réservés.
          </p>
          <p class="text-gray-400 text-xs">
            Powered by
            <a href="https://digita.sn" target="_blank" class="text-primary font-semibold hover:text-primary-dark transition-colors">Digita SN</a>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, reactive, ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/store/modules/auth'
import { useToastStore } from '@/store/modules/toastStore'
import { usePermissionsStore } from '@/store/modules/permissions'
import { findFirstAllowedRoute } from '@/router/guards'
import physicalInventoryService from '@/services/physicalInventoryService'
import packageJson from '../../../package.json'

export default defineComponent({
    name: 'LoginView',

    setup() {
        const authStore = useAuthStore()
        const toastStore = useToastStore()
        const permissionsStore = usePermissionsStore()
        const router = useRouter()
        const route = useRoute()

        const form = reactive({
            email: '',
            password: ''
        })

        const isLoading = ref(false)
        const error = ref(null)
        const validationErrors = ref({})
        const showPassword = ref(false)
        const version = ref(packageJson.version)

        const togglePasswordVisibility = () => {
            showPassword.value = !showPassword.value
        }

        const isMobileDevice = () => {
            return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                   window.innerWidth <= 768
        }

        const checkOngoingInventory = async () => {
            try {
                const response = await physicalInventoryService.getInventories({
                    status: 'in_progress',
                    per_page: 1,
                    page: 1
                })

                return response.data && response.data.length > 0
            } catch (error) {
                console.error('Erreur lors de la vérification de l\'inventaire:', error)
                return false
            }
        }

        const handleLogin = async () => {
            isLoading.value = true
            error.value = null
            validationErrors.value = {}

            try {
                await authStore.login({
                    email: form.email,
                    phone: form.email,
                    password: form.password
                })

                // Charger les permissions de l'utilisateur
                await permissionsStore.fetchPermissions()

                console.log('isMobileDevice():', isMobileDevice())
                if (isMobileDevice()) {
                    const hasOngoingInventory = await checkOngoingInventory()
                    console.log('hasOngoingInventory():', hasOngoingInventory)
                    if (hasOngoingInventory) {
                        router.push({ name: 'doing-inventory' })
                        return
                    }
                }
                // Rediriger vers la page demandée ou la première page autorisée
                if (route.query.redirect) {
                    router.push(route.query.redirect)
                } else {
                    router.push(findFirstAllowedRoute())
                }
            } catch (err) {
                console.error('Login error:', err)

                if (err.response && err.response.status === 422) {
                    validationErrors.value = err.response.data.errors || {}
                    error.value = err.response.data.message || 'Erreur de validation'
                }
                else if (err.response && err.response.status === 401) {
                    error.value = 'Email ou mot de passe incorrect'
                }
                else if (err.response && err.response.data && err.response.data.verification_needed) {
                    error.value = "Votre email n'est pas vérifié. Veuillez vérifier votre boîte mail."

                    toastStore.addToast({
                        type: 'info',
                        message: "Vous pouvez demander un nouvel email de vérification",
                        duration: 5000
                    })
                }
                else {
                    error.value = err.response?.data?.message || 'Une erreur est survenue lors de la connexion'
                }
            } finally {
                isLoading.value = false
            }
        }

        return {
            form,
            isLoading,
            error,
            validationErrors,
            showPassword,
            togglePasswordVisibility,
            handleLogin,
            version
        }
    }
})
</script>

<style scoped>
.dot-grid {
  background-image: radial-gradient(circle, rgba(255, 255, 255, 0.8) 1px, transparent 1px);
  background-size: 30px 30px;
}

.orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.4;
}

.orb-1 {
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(13, 148, 136, 0.5), transparent 70%);
  top: -10%;
  right: 20%;
  animation: float-1 20s ease-in-out infinite;
}

.orb-2 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(16, 185, 129, 0.4), transparent 70%);
  bottom: -5%;
  left: 10%;
  animation: float-2 25s ease-in-out infinite;
}

.orb-3 {
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba(59, 130, 246, 0.3), transparent 70%);
  top: 40%;
  left: 40%;
  animation: float-3 18s ease-in-out infinite;
}

@keyframes float-1 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(40px, -30px) scale(1.1); }
  66% { transform: translate(-20px, 20px) scale(0.95); }
}

@keyframes float-2 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(-30px, 40px) scale(1.05); }
  66% { transform: translate(30px, -20px) scale(0.9); }
}

@keyframes float-3 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(20px, 30px) scale(1.15); }
  66% { transform: translate(-40px, -10px) scale(0.85); }
}

@media (max-width: 1023px) {
  .min-h-screen {
    background: white;
  }
}
</style>
